import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormFooter } from '@/components/ui/form-footer';
import { Input } from '@/components/ui/input';
import { ChoiceSelect } from '@/components/ui/choice-select';
import { AciSearch } from './AciSearch';
import {
  createVehicle,
  FUELS,
  patchVehicle,
  type Fuel,
  type ListedVehicle,
  type Vehicle,
} from './api';

/**
 * A vehicle's fields: the label, the fuel, the plate and the ACI reference (the table row its €/km
 * comes from). Creates when `vehicle` is undefined, else changes that one. Calls `onSaved` with the
 * stored vehicle.
 */
export function VehicleForm({
  vehicle,
  searchAci,
  onSaved,
  onCancel,
}: {
  readonly vehicle?: ListedVehicle;
  readonly searchAci: boolean;
  readonly onSaved: (vehicle: Vehicle) => void;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation('expenses');
  const [label, setLabel] = useState(vehicle?.label ?? '');
  const [fuel, setFuel] = useState<Fuel | ''>((vehicle?.fuel as Fuel | null) ?? '');
  const [plate, setPlate] = useState(vehicle?.plate ?? '');
  const [aciRef, setAciRef] = useState(vehicle?.aciVehicleRef ?? '');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const save = async (): Promise<void> => {
    if (label.trim() === '' || busy) return;
    setBusy(true);
    setFailed(false);
    const body = {
      label: label.trim(),
      fuel: fuel === '' ? null : fuel,
      plate: plate.trim() === '' ? null : plate.trim(),
      aciVehicleRef: aciRef.trim() === '' ? null : aciRef.trim(),
    };
    const outcome = vehicle ? await patchVehicle(vehicle.id, body) : await createVehicle(body);
    setBusy(false);
    if (outcome.ok) onSaved(outcome.value);
    else setFailed(true);
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <label className="flex flex-col gap-1 text-sm font-semibold">
        {t('mileage.vehicles.label')}
        <Input
          value={label}
          maxLength={120}
          placeholder={t('mileage.vehicles.labelPlaceholder')}
          onChange={(event) => setLabel(event.target.value)}
        />
      </label>
      <FuelPlateAci
        {...{ fuel, setFuel, plate, setPlate, aciRef, setAciRef, label, setLabel, searchAci }}
      />
      {failed ? (
        <p role="alert" className="text-destructive text-sm">
          {t('mileage.vehicles.failed')}
        </p>
      ) : null}
      <FormFooter
        reason={label.trim() === '' ? t('mileage.vehicles.saveReason') : null}
        primaryLabel={t('mileage.vehicles.save')}
        primaryDisabled={label.trim() === '' || busy}
        cancelLabel={t('mileage.cancel')}
        onCancel={onCancel}
      />
    </form>
  );
}

type ExtraProps = {
  readonly fuel: Fuel | '';
  readonly setFuel: (fuel: Fuel | '') => void;
  readonly plate: string;
  readonly setPlate: (plate: string) => void;
  readonly aciRef: string;
  readonly setAciRef: (ref: string) => void;
  readonly label: string;
  readonly setLabel: (label: string) => void;
  readonly searchAci: boolean;
};

/** The fuel, the plate and the ACI reference, with the ACI search (Gestione) beneath. */
function FuelPlateAci(props: ExtraProps) {
  const { fuel, setFuel, plate, setPlate, aciRef, setAciRef, label, setLabel, searchAci } = props;
  const { t } = useTranslation('expenses');
  const uid = useId();
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1 text-sm font-semibold">
          <label htmlFor={`${uid}-fuel`}>{t('mileage.vehicles.fuel')}</label>
          <ChoiceSelect
            id={`${uid}-fuel`}
            value={fuel}
            onChange={(value) => setFuel(value as Fuel | '')}
            choices={[
              { value: '', label: t('mileage.vehicles.fuelNone') },
              ...FUELS.map((value) => ({ value, label: t(`mileage.vehicles.fuels.${value}`) })),
            ]}
          />
        </div>
        <label className="flex flex-col gap-1 text-sm font-semibold">
          {t('mileage.vehicles.plate')}
          <Input value={plate} maxLength={20} onChange={(event) => setPlate(event.target.value)} />
        </label>
      </div>
      <label className="flex flex-col gap-1 text-sm font-semibold">
        {t('mileage.vehicles.aciRef')}
        <Input value={aciRef} maxLength={80} onChange={(event) => setAciRef(event.target.value)} />
      </label>
      {searchAci ? (
        <AciSearch
          onPick={(tariff) => {
            setAciRef(tariff.aciVehicleRef);
            if (label.trim() === '') setLabel(`${tariff.make} ${tariff.model}`);
            if (fuel === '')
              setFuel(
                (FUELS as readonly string[]).includes(tariff.fuel) ? (tariff.fuel as Fuel) : '',
              );
          }}
        />
      ) : null}
    </>
  );
}
